import React, { useState, useEffect } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import { Modal } from '../ui/Modal';
import { Button, IconButton } from '../ui';
import ConfirmDialog from '../ui/ConfirmDialog';
import db from '../../lib/database';
import { supabase } from '../../lib/supabase';

function getStateAbbr(address) {
  if (!address) return null;
  const match = address.match(/,\s*([A-Z]{2})\s*\d{5}(?:-\d{4})?\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*\d{0,5}\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*,/)
    || address.match(/\b([A-Z]{2})\s+\d{5}/);
  return match ? match[1] : null;
}

// Mint a signed dispatch-view URL via the server endpoint.
// Server side owns DISPATCH_LINK_SECRET; this fetch passes the office user's
// Supabase JWT for auth. Replaces the previous browser-side HMAC generation
// (which had the secret bundled into the frontend — see 2026-05-14 security
// audit handoff). Returns null + logs on failure so the SMS draft can still
// be sent without the link (gracefully degrades, doesn't blow up the whole
// daily dispatch flow).
async function mintDispatchViewUrl(jobId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const jwt = session?.access_token;
    if (!jwt) {
      console.warn('Dispatch token mint skipped: no Supabase session');
      return null;
    }
    const resp = await fetch('/api/dispatch-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ jobId }),
    });
    if (!resp.ok) {
      console.warn('Dispatch token mint failed:', resp.status);
      return null;
    }
    const { token, expiresAt } = await resp.json();
    const base = window.location.origin;
    return `${base}/api/dispatch-view?job=${encodeURIComponent(jobId)}&exp=${expiresAt}&token=${token}`;
  } catch (e) {
    console.warn('Dispatch token mint error:', e);
    return null;
  }
}

function getCrewPortalUrl(portalToken) {
  const base = window.location.origin;
  return `${base}/crew?token=${portalToken}`;
}

function buildCrewLeadDispatch(dayJobs, crew, clients, trucks, dispatchLinks, portalLink) {
  const crewOnJobs = new Set();
  dayJobs.forEach(j => (j.crew_ids || []).forEach(id => { if (id !== j.leader_id) crewOnJobs.add(id); }));
  const crewNames = [...crewOnJobs].map(id => crew.find(c => c.id === id)?.name).filter(Boolean);
  const allTruckIds = [...new Set(dayJobs.flatMap(j => j.truck_ids || []))];
  const truckNames = allTruckIds.map(id => trucks.find(t => t.id === id)?.name).filter(Boolean);

  let msg = `BEATY MOVING — Tomorrow's Jobs\n\n`;
  if (crewNames.length) msg += `Hi, tomorrow you have ${crewNames.join(', ')}.`;
  if (truckNames.length) msg += ` Pls take ${truckNames.join(' and ')}.`;
  msg += '\n';

  dayJobs.sort((a, b) => (a.day_sequence || 1) - (b.day_sequence || 1));

  dayJobs.forEach((job, idx) => {
    const client = clients.find(c => c.id === job.client_id);
    msg += `\n********\n`;
    if (dayJobs.length > 1) msg += `${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'} Job: `;
    else msg += `Job: `;
    msg += `${job.job_label || job.type.replace('_', ' ')}\n\n`;
    msg += `   ${client?.name || 'TBD'}\n`;
    if (job.location_name) msg += `   ${job.location_name}\n`;
    msg += `   ${job.from_address || 'TBD'}\n`;
    const jobClient = clients.find(c => c.id === job.client_id);
    if (jobClient?.additional_stops) {
      try {
        const stops = JSON.parse(jobClient.additional_stops);
        if (Array.isArray(stops)) stops.forEach((s, si) => {
          if (s.address) msg += `Stop ${si + 2}:\n   ${s.address}\n`;
        });
      } catch(e) {}
    }
    msg += `Move TO:\n`;
    msg += `   ${job.to_address || 'TBD'}\n`;
    if (job.heavy_items) msg += `\n${job.heavy_items}\n`;
    if (job.special_notes) msg += `${job.special_notes}\n`;
    if (job.dispatcher_notes) msg += `${job.dispatcher_notes}\n`;
    if (job.stairs) msg += `Has stairs (${job.levels || '?'} levels)\n`;
  });

  if (portalLink) {
    msg += `\n━━━━━━━━\nYour Daily Portal:\n${portalLink}\n(View all jobs, photos, update status & submit recaps)\n`;
  }

  return msg;
}

// Non-lead crew get the MINIMAL message (Jaylen 2026-06-09): who their
// lead is, nothing else — no addresses, no roster. The lead carries the
// details; less in the text means less stale info floating around when
// dispatch changes after send. Start time kept as the one practical
// must-know.
function buildCrewMemberDispatch(dayJobs, memberId, crew, clients, trucks) {
  const leader = crew.find(c => c.id === dayJobs[0]?.leader_id);
  let msg = `BEATY MOVING — Tomorrow\n\n`;
  msg += `${dayJobs[0]?.time || '8:00'} start\n`;
  msg += `You're with ${leader?.name || 'your crew lead'} — see them for details.`;
  return msg;
}

export default function DailyDispatchModal({ dateStr, crew, jobs, clients, trucks, onClose, onSent, onError }) {
  const [selectedPrev, setSelectedPrev] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmUnassigned, setConfirmUnassigned] = useState(false);
  const [targetDate, setTargetDate] = useState(dateStr);
  const [dispatchLinks, setDispatchLinks] = useState({});
  const [portalLinks, setPortalLinks] = useState({});

  const displayDate = parseISO(targetDate);
  const dayJobs = jobs.filter(j => {
    if (j.move_date === targetDate && (j.crew_ids || []).length > 0) return true;
    if ((j.duration_days || 1) > 1 && j.move_date < targetDate) {
      const end = addDays(parseISO(j.move_date), j.duration_days - 1);
      return parseISO(targetDate) <= end;
    }
    return false;
  });

  useEffect(() => {
    async function genLinks() {
      const links = {};
      for (const j of dayJobs) {
        const url = await mintDispatchViewUrl(j.id);
        if (url) links[j.id] = url;
      }
      setDispatchLinks(links);

      const portal = {};
      const leadIds = [...new Set(dayJobs.map(j => j.leader_id).filter(Boolean))];
      for (const leadId of leadIds) {
        const leader = crew.find(c => c.id === leadId);
        // Crew migrated to email/password (auth_user_id set) get a TOKENLESS
        // /crew link — they sign in (or land straight in on their installed
        // PWA). A ?token= would route them to the legacy token+PIN path and
        // wrongly prompt for a PIN. Un-migrated crew still get the tokenized
        // PIN link during the transition.
        if (leader?.auth_user_id) {
          portal[leadId] = `${window.location.origin}/crew`;
          continue;
        }
        try {
          const portalToken = await db.generatePortalToken(leadId, targetDate);
          portal[leadId] = getCrewPortalUrl(portalToken);
        } catch (e) { console.log('Portal token gen failed:', e); }
      }
      setPortalLinks(portal);
    }
    if (dayJobs.length > 0) genLinks();
  }, [targetDate, dayJobs.length]);

  const leadGroups = {};
  dayJobs.forEach(j => {
    const leadId = j.leader_id;
    if (!leadId) return;
    if (!leadGroups[leadId]) leadGroups[leadId] = [];
    leadGroups[leadId].push(j);
  });

  const previews = Object.entries(leadGroups).map(([leadId, leadJobs]) => {
    const leader = crew.find(c => c.id === leadId);
    if (!leader) return null;
    const portalLink = portalLinks[leadId] || null;
    const msg = buildCrewLeadDispatch(leadJobs, crew, clients, trucks, dispatchLinks, portalLink);
    return { leader, jobs: leadJobs, msg, hasPhone: !!leader.phone, portalLink };
  }).filter(Boolean);

  const memberMessages = [];
  dayJobs.forEach(j => {
    (j.crew_ids || []).forEach(cid => {
      if (cid === j.leader_id) return;
      if (memberMessages.find(m => m.memberId === cid)) return;
      const member = crew.find(c => c.id === cid);
      if (!member) return;
      const memberJobs = dayJobs.filter(dj => (dj.crew_ids || []).includes(cid));
      const msg = buildCrewMemberDispatch(memberJobs, cid, crew, clients, trucks);
      memberMessages.push({ memberId: cid, member, msg, hasPhone: !!member.phone });
    });
  });

  const allSendable = [
    ...previews.filter(p => p.hasPhone).map(p => ({ to: p.leader.phone, body: p.msg, name: p.leader.name, role: 'Lead' })),
    ...memberMessages.filter(m => m.hasPhone).map(m => ({ to: m.member.phone, body: m.msg, name: m.member.name, role: 'Crew' })),
  ];

  const handleSendAll = async () => {
    setSending(true);
    try {
      const messages = allSendable.map(s => ({ to: s.to, body: s.body }));
      const response = await fetch('/api/dispatch-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed');

      // ── Daily-dispatch gate (sql/039_daily_dispatch_sent.sql) ──
      // Record the release for this date and clear any pre-dispatch
      // push backlog so the crew doesn't get duplicate "Job updated"
      // pings on top of the SMS that just went out. Failure here is
      // non-fatal — the SMS already sent successfully, so we surface
      // it as a soft warning rather than rolling back the user's
      // workflow. Future edits on these jobs will queue and flush
      // through the normal pipeline (date is now "released").
      try {
        await db.markDayDispatched(targetDate);
      } catch (gateErr) {
        console.warn('markDayDispatched failed (non-fatal):', gateErr?.message || gateErr);
      }

      // A full-day (re)send covers every open changes-since-send entry
      // for this date (sql/074) — the crews just got the fresh lineup.
      // Non-fatal for the same reason as above.
      try {
        await db.clearDispatchChanges({ date: targetDate, reason: 'resend' });
      } catch (clearErr) {
        console.warn('clearDispatchChanges (resend) failed (non-fatal):', clearErr?.message || clearErr);
      }

      onSent(`Daily dispatch sent to ${data.sent} crew member${data.sent !== 1 ? 's' : ''}${data.failed ? ` (${data.failed} failed)` : ''}`);
    } catch (e) { onError('Error: ' + e.message); }
    finally { setSending(false); }
  };

  // Non-blocking pre-send check: dispatched jobs that still have no crew, or
  // (for truck jobs) no truck assigned. Warns the dispatcher but still sends.
  const jobsMissingResources = dayJobs.filter(j => {
    if (j.type === 'WALKTHROUGH' || j.status === 'cancelled') return false;
    if (!j.leader_id && (j.crew_ids || []).length === 0) return false; // not on the board for dispatch
    const noCrew = (j.crew_ids || []).length === 0;
    const truckJob = j.type === 'LOCAL' || j.type === 'LONG_DISTANCE' || j.type === 'DELIVERY';
    const noTruck = truckJob && (j.truck_ids || []).length === 0;
    return noCrew || noTruck;
  });

  const requestSend = () => {
    if (jobsMissingResources.length > 0) setConfirmUnassigned(true);
    else handleSendAll();
  };

  const isToday = targetDate === format(new Date(), 'yyyy-MM-dd');
  const isTomorrow = targetDate === format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const dayLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : format(displayDate, 'EEEE');

  return (
    <Modal title={`Daily Dispatch — ${dayLabel}, ${format(displayDate, 'MMM d')}`} onClose={onClose} width="max-w-3xl">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-slate-600 flex-1">Detailed job info sent to crew leads + simplified message to crew members.</p>
          <div className="flex items-center gap-1">
            <IconButton label="Previous day" size="sm" onClick={() => setTargetDate(format(addDays(parseISO(targetDate), -1), 'yyyy-MM-dd'))}>←</IconButton>
            <Button variant="secondary" size="sm" onClick={() => setTargetDate(format(new Date(), 'yyyy-MM-dd'))}>Today</Button>
            <Button variant="secondary" size="sm" onClick={() => setTargetDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'))}>Tmrw</Button>
            <IconButton label="Next day" size="sm" onClick={() => setTargetDate(format(addDays(parseISO(targetDate), 1), 'yyyy-MM-dd'))}>→</IconButton>
          </div>
        </div>

        {previews.length === 0 && memberMessages.length === 0 && (
          <div className="text-center py-8 text-slate-400">No jobs scheduled for {dayLabel}.</div>
        )}

        {previews.length > 0 && (
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Crew Leads</h3>
            <div className="space-y-2">
              {previews.map(p => (
                <div key={p.leader.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedPrev(selectedPrev === p.leader.id ? null : p.leader.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.leader.display_color }}></div>
                      <span className="font-medium text-sm">{p.leader.name}</span>
                      <span className="text-xs text-slate-500">({p.jobs.length} job{p.jobs.length !== 1 ? 's' : ''})</span>
                      {p.hasPhone ? <span className="text-xs text-green-600">✓ {p.leader.phone}</span> : <span className="text-xs text-red-500">No phone</span>}
                    </div>
                    <span className="text-xs text-slate-400">{selectedPrev === p.leader.id ? '▲ Hide' : '▼ Preview'}</span>
                  </div>
                  {selectedPrev === p.leader.id && (
                    <pre className="mt-2 p-3 bg-slate-900 text-green-400 text-xs rounded-lg whitespace-pre-wrap font-mono overflow-auto max-h-48">{p.msg}</pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {memberMessages.length > 0 && (
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Crew Members</h3>
            <div className="space-y-2">
              {memberMessages.map(m => (
                <div key={m.memberId} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedPrev(selectedPrev === m.memberId ? null : m.memberId)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.member.display_color }}></div>
                      <span className="font-medium text-sm">{m.member.name}</span>
                      {m.hasPhone ? <span className="text-xs text-green-600">✓ {m.member.phone}</span> : <span className="text-xs text-red-500">No phone</span>}
                    </div>
                    <span className="text-xs text-slate-400">{selectedPrev === m.memberId ? '▲ Hide' : '▼ Preview'}</span>
                  </div>
                  {selectedPrev === m.memberId && (
                    <pre className="mt-2 p-3 bg-slate-900 text-green-400 text-xs rounded-lg whitespace-pre-wrap font-mono overflow-auto max-h-48">{m.msg}</pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(() => {
          const noPhone = [...previews.filter(p => !p.hasPhone).map(p => p.leader.name), ...memberMessages.filter(m => !m.hasPhone).map(m => m.member.name)];
          return noPhone.length > 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
              {noPhone.length} crew without phone: {noPhone.join(', ')}
            </div>
          ) : null;
        })()}

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={sending}
            disabled={allSendable.length === 0}
            onClick={requestSend}
          >
            📱 Send to {allSendable.length} Crew
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmUnassigned}
        title={`Send ${format(displayDate, 'EEE, MMM d')}'s dispatch with ${jobsMissingResources.length} unstaffed job${jobsMissingResources.length !== 1 ? 's' : ''}?`}
        confirmLabel="Send anyway"
        cancelLabel="Go back"
        onConfirm={() => { setConfirmUnassigned(false); handleSendAll(); }}
        onCancel={() => setConfirmUnassigned(false)}>
        {jobsMissingResources.length} job{jobsMissingResources.length !== 1 ? 's' : ''} on this day still {jobsMissingResources.length !== 1 ? 'have' : 'has'} no crew and/or truck assigned. The {allSendable.length} staffed crew{allSendable.length !== 1 ? 's' : ''} get texted now; the unstaffed jobs stay on the board until you assign them.
      </ConfirmDialog>
    </Modal>
  );
}
